/* eslint react-hooks/exhaustive-deps: "warn" */

import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useIntersection } from 'hooks/useIntersection';

import styles from './FinancialsCreditCard.module.scss';

import type { FinancialsCreditCardProps } from './FinancialsCreditCard.types';

const YEARS = ['2021', '2022', '2023', '2024', '2025'];

type StatRow = { label: string; values: string[]; trend: number[] };

const ROWS: StatRow[] = [
  { label: 'Turnover', values: ['36.1M', '38.9M', '40.4M', '43.0M', '48.2M'], trend: [22, 40, 50, 66, 100] },
  { label: 'EBITDA', values: ['5.2M', '5.9M', '6.1M', '6.8M', '7.9M'], trend: [22, 42, 48, 68, 100] },
  { label: 'Profit after tax', values: ['2.4M', '2.9M', '3.1M', '3.5M', '4.1M'], trend: [22, 45, 54, 72, 100] },
  { label: 'Total assets', values: ['44.8M', '49.2M', '52.6M', '56.9M', '61.5M'], trend: [22, 43, 58, 79, 100] },
  { label: 'Net assets', values: ['18.2M', '20.4M', '22.7M', '25.6M', '28.9M'], trend: [22, 38, 55, 76, 100] },
];

const ROW_START_S = 1.8;
const ROW_STAGGER_S = 0.22;
const CREDIT_START_S = ROW_START_S + ROWS.length * ROW_STAGGER_S + 0.3;
const STAMP_START_S = CREDIT_START_S + 1.3;

const CREDIT_SCORE = 72;
const CREDIT_LIMIT = 250_000;
const CREDIT_DAYS = 12;
const COUNT_MS = 1000;

const easeOut = (t: number) => 1 - (1 - t) ** 3;

export const FinancialsCreditCard = ({ className }: FinancialsCreditCardProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const played = useIntersection(ref, { threshold: 0.35 });

  const [reduced, setReduced] = useState(false);
  const [score, setScore] = useState(0);
  const [limit, setLimit] = useState(0);
  const [days, setDays] = useState(0);

  useEffect(() => {
    setReduced(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }, []);

  useEffect(() => {
    if (!played) {
      return undefined;
    }

    if (reduced) {
      setScore(CREDIT_SCORE);
      setLimit(CREDIT_LIMIT);
      setDays(CREDIT_DAYS);

      return undefined;
    }

    let raf = 0;
    const start = window.setTimeout(() => {
      const t0 = performance.now();

      const tick = (now: number) => {
        const p = Math.min((now - t0) / COUNT_MS, 1);
        const eased = easeOut(p);

        setScore(Math.round(eased * CREDIT_SCORE));
        setLimit(Math.round(eased * CREDIT_LIMIT));
        setDays(Math.round(eased * CREDIT_DAYS));

        if (p < 1) {
          raf = requestAnimationFrame(tick);
        }
      };

      raf = requestAnimationFrame(tick);
    }, CREDIT_START_S * 1000);

    return () => {
      window.clearTimeout(start);
      cancelAnimationFrame(raf);
    };
  }, [played, reduced]);

  return (
    <div ref={ref} className={cn(styles.card, played && styles.play, reduced && styles.instant, className)}>
      <div className={styles.head}>
        <span className={styles.dot} />
        <span className={styles.headTitle}>Filed statements &amp; credit — Acme Holdings GmbH</span>
      </div>

      <div className={styles.body}>
        <div className={styles.doc} style={{ transitionDelay: '0.2s' }}>
          <span className={styles.docBadge}>PDF</span>
          <span>Jahresabschluss 2025 · filed with Handelsregister</span>
        </div>

        <div className={styles.scanWrap} style={{ transitionDelay: '0.6s' }}>
          <span className={styles.scanLabel}>Digitising with OCR + AI…</span>
          <div className={styles.scanTrack}>
            <div className={styles.scanFill} style={{ transitionDelay: '0.6s' }} />
          </div>
        </div>

        <div className={styles.table}>
          <div className={styles.tableMeta}>
            <span>EUR · consolidated · 12-month periods</span>
            <span>20 years on file</span>
          </div>

          <div className={cn(styles.row, styles.rowHead)}>
            <span className={styles.label}>Date of report</span>
            {YEARS.map((year) => (
              <span key={year} className={styles.year}>
                {year}
              </span>
            ))}
            <span className={styles.trendHead}>5-yr trend</span>
          </div>

          {ROWS.map((row, i) => (
            <div key={row.label} className={styles.row} style={{ transitionDelay: `${ROW_START_S + i * ROW_STAGGER_S}s` }}>
              <span className={styles.label}>{row.label}</span>
              {row.values.map((value, vi) => (
                <span key={vi} className={styles.value}>
                  {value}
                </span>
              ))}
              <span className={styles.trend}>
                {row.trend.map((height, hi) => (
                  <i key={hi} style={{ height: `${height}%` }} />
                ))}
              </span>
            </div>
          ))}
        </div>

        <div className={styles.creditStrip} style={{ transitionDelay: `${CREDIT_START_S}s` }}>
          <div className={styles.tile}>
            <svg className={styles.gauge} viewBox="0 0 200 110" aria-hidden="true">
              <path className={styles.gaugeBg} d="M20 100 A80 80 0 0 1 180 100" />
              <path
                className={styles.gaugeFg}
                d="M20 100 A80 80 0 0 1 180 100"
                pathLength={100}
                style={{
                  strokeDashoffset: played ? 100 - CREDIT_SCORE : 100,
                  transitionDelay: `${CREDIT_START_S}s`,
                }}
              />
              <text className={styles.gaugeScore} x="100" y="96" textAnchor="middle">
                {score}
              </text>
            </svg>
            <span className={styles.tileLabel}>Credit score</span>
            <span className={cn(styles.tileSub, styles.tileSubOk)}>Low risk · out of 100</span>
          </div>

          <div className={styles.tile}>
            <span className={styles.tileValue}>€{limit.toLocaleString('en-US')}</span>
            <span className={styles.tileLabel}>Credit limit</span>
            <span className={styles.tileSub}>Recommended, on filed accounts</span>
          </div>

          <div className={styles.tile}>
            <span className={styles.tileValue}>{days} days</span>
            <span className={styles.tileLabel}>Payment behaviour</span>
            <span className={styles.tileSub}>Beyond agreed terms, trailing 12m</span>
          </div>
        </div>

        <div className={styles.stamp} style={{ transitionDelay: `${STAMP_START_S}s` }}>
          Every figure from the filed accounts · original PDF attached
        </div>
      </div>
    </div>
  );
};
